import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { predefinedCategories } from '@/lib/categories';
import { formatCurrency, formatDate } from '@/lib/utils';
import DataManagementService, {
  type DataManagementResponse,
  type DataManagementParams,
} from '@/services/DataManagementService';
import jsPDF from 'jspdf';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  Download,
  Edit,
  Filter,
  Search,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

interface Asset {
  id: string;
  name: string;
  address: string;
  asset_type: string;
  purchase_price: number;
  market_value: number;
  market_rent: number;
  total_income: number;
  total_expenses: number;
  net_cash_flow: number;
  cap_rate: number;
  investment_status: string;
}

interface Transaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  transaction_type: 'INCOME' | 'EXPENSE';
  category: string;
  payee: string;
  property_name: string;
  status: string;
}

interface FinancialSummary {
  id: string;
  property_name: string;
  address: string;
  purchase_price: number;
  market_value: number;
  total_income: number;
  total_expenses: number;
  net_cash_flow: number;
  cap_rate: number;
  cash_on_cash_return: number;
  appreciation_percentage: number;
}

interface Filters {
  status?: string;
  propertyType?: string;
  transactionType?: string;
  category?: string;
  startDate?: string;
  endDate?: string;
  period?: string;
}

export function DataManagementPage() {
  const [activeTab, setActiveTab] = useState('assets');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [filters, setFilters] = useState<Filters>({});
  const [data, setData] = useState<(Asset | Transaction | FinancialSummary)[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: DataManagementParams = {
        page: currentPage,
        limit: 10,
        search: searchTerm,
        sortBy,
        sortOrder,
        ...filters,
      };

      let response: DataManagementResponse<
        Asset | Transaction | FinancialSummary
      >;
      switch (activeTab) {
        case 'assets':
          response = await DataManagementService.getAssetsTable(params);
          break;
        case 'transactions':
          response = await DataManagementService.getTransactionsTable(params);
          break;
        case 'financials':
          response =
            await DataManagementService.getFinancialSummaryTable(params);
          break;
        default:
          response = await DataManagementService.getAssetsTable(params);
      }

      setData(response.data || []);
      setPagination(response.pagination || { total: 0, totalPages: 0 });
    } catch (error) {
      console.error('Error fetching data:', error);
      // Set default values on error
      setData([]);
      setPagination({ total: 0, totalPages: 0 });
    } finally {
      setLoading(false);
    }
  }, [activeTab, currentPage, searchTerm, sortBy, sortOrder, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    try {
      // Use existing data for PDF generation
      console.log('Using existing data for PDF:', data);
      await generatePDFReport(data, activeTab);
    } catch (error) {
      console.error('Error exporting data:', error);
      alert(`Failed to export data: ${(error as Error).message}`);
    }
  };

  const generatePDFReport = async (
    data: (Asset | Transaction | FinancialSummary)[],
    reportType: string,
  ) => {
    try {
      console.log('Generating PDF for:', reportType, 'with data:', data);

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      let yPosition = 20;

      // Header with BetterCapital.us branding
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text('BetterCapital.us', pageWidth / 2, yPosition, {
        align: 'center',
      });
      yPosition += 10;

      // Subtitle
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text('Real Estate Investment Management', pageWidth / 2, yPosition, {
        align: 'center',
      });
      yPosition += 20;

      // Divider line
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 15;

      // Report Title
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      const title = `${reportType.charAt(0).toUpperCase() + reportType.slice(1)} Report`;
      doc.text(title, margin, yPosition);
      yPosition += 15;

      // Date
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      const date = new Date().toLocaleDateString();
      doc.text(`Generated: ${date}`, margin, yPosition);
      yPosition += 10;

      // Data count
      const dataArray = Array.isArray(data) ? data : [];
      doc.text(`Total Records: ${dataArray.length}`, margin, yPosition);
      yPosition += 15;

      if (dataArray.length === 0) {
        doc.text('No data available for this report.', margin, yPosition);
      } else {
        doc.setFontSize(10);

        switch (reportType) {
          case 'assets':
            (dataArray as Asset[]).forEach((asset: Asset, index: number) => {
              if (yPosition > 250) {
                // Add footer to current page
                addFooter(doc, pageWidth);
                doc.addPage();
                yPosition = 20;
                // Add header to new page
                addHeader(doc, pageWidth);
                yPosition = 40;
              }

              doc.setFont('helvetica', 'bold');
              doc.text(
                `${index + 1}. ${asset.name || 'N/A'}`,
                margin,
                yPosition,
              );
              yPosition += 5;

              doc.setFont('helvetica', 'normal');
              doc.text(
                `   Address: ${asset.address || 'N/A'}`,
                margin,
                yPosition,
              );
              yPosition += 5;
              doc.text(
                `   Type: ${asset.asset_type || 'N/A'}`,
                margin,
                yPosition,
              );
              yPosition += 5;
              doc.text(
                `   Purchase Price: ${formatCurrency(asset.purchase_price || 0)}`,
                margin,
                yPosition,
              );
              yPosition += 5;
              doc.text(
                `   Market Value: ${formatCurrency(asset.market_value || 0)}`,
                margin,
                yPosition,
              );
              yPosition += 5;
              doc.text(
                `   Net Cash Flow: ${formatCurrency(asset.net_cash_flow || 0)}`,
                margin,
                yPosition,
              );
              yPosition += 5;
              doc.text(
                `   Cap Rate: ${((asset.cap_rate || 0) * 100).toFixed(2)}%`,
                margin,
                yPosition,
              );
              yPosition += 10;
            });
            break;

          case 'transactions':
            (dataArray as Transaction[]).forEach(
              (transaction: Transaction, index: number) => {
                if (yPosition > 250) {
                  // Add footer to current page
                  addFooter(doc, pageWidth);
                  doc.addPage();
                  yPosition = 20;
                  // Add header to new page
                  addHeader(doc, pageWidth);
                  yPosition = 40;
                }

                doc.setFont('helvetica', 'bold');
                doc.text(
                  `${index + 1}. ${transaction.description || 'N/A'}`,
                  margin,
                  yPosition,
                );
                yPosition += 5;

                doc.setFont('helvetica', 'normal');
                doc.text(
                  `   Date: ${formatDate(transaction.date) || 'N/A'}`,
                  margin,
                  yPosition,
                );
                yPosition += 5;
                doc.text(
                  `   Amount: ${formatCurrency(transaction.amount || 0)}`,
                  margin,
                  yPosition,
                );
                yPosition += 5;
                doc.text(
                  `   Type: ${transaction.transaction_type || 'N/A'}`,
                  margin,
                  yPosition,
                );
                yPosition += 5;
                doc.text(
                  `   Category: ${transaction.category || 'N/A'}`,
                  margin,
                  yPosition,
                );
                yPosition += 5;
                doc.text(
                  `   Property: ${transaction.property_name || 'N/A'}`,
                  margin,
                  yPosition,
                );
                yPosition += 10;
              },
            );
            break;

          case 'financials':
            (dataArray as FinancialSummary[]).forEach(
              (financial: FinancialSummary, index: number) => {
                if (yPosition > 250) {
                  // Add footer to current page
                  addFooter(doc, pageWidth);
                  doc.addPage();
                  yPosition = 20;
                  // Add header to new page
                  addHeader(doc, pageWidth);
                  yPosition = 40;
                }

                doc.setFont('helvetica', 'bold');
                doc.text(
                  `${index + 1}. ${financial.property_name || 'N/A'}`,
                  margin,
                  yPosition,
                );
                yPosition += 5;

                doc.setFont('helvetica', 'normal');
                doc.text(
                  `   Address: ${financial.address || 'N/A'}`,
                  margin,
                  yPosition,
                );
                yPosition += 5;
                doc.text(
                  `   Total Income: ${formatCurrency(financial.total_income || 0)}`,
                  margin,
                  yPosition,
                );
                yPosition += 5;
                doc.text(
                  `   Total Expenses: ${formatCurrency(financial.total_expenses || 0)}`,
                  margin,
                  yPosition,
                );
                yPosition += 5;
                doc.text(
                  `   Net Cash Flow: ${formatCurrency(financial.net_cash_flow || 0)}`,
                  margin,
                  yPosition,
                );
                yPosition += 5;
                doc.text(
                  `   Cap Rate: ${((financial.cap_rate || 0) * 100).toFixed(2)}%`,
                  margin,
                  yPosition,
                );
                yPosition += 5;
                doc.text(
                  `   Cash on Cash: ${((financial.cash_on_cash_return || 0) * 100).toFixed(2)}%`,
                  margin,
                  yPosition,
                );
                yPosition += 10;
              },
            );
            break;

          default:
            doc.text('No data available for this report.', margin, yPosition);
        }
      }

      // Add footer to last page
      addFooter(doc, pageWidth);

      // Save the PDF
      const filename = `BetterCapital_${reportType}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(filename);

      console.log('PDF generated successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert(`Failed to generate PDF: ${(error as Error).message}`);
    }
  };

  const addHeader = (doc: jsPDF, pageWidth: number) => {
    const margin = 20;
    let yPosition = 20;

    // Header with BetterCapital.us branding
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('BetterCapital.us', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 8;

    // Subtitle
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text('Real Estate Investment Management', pageWidth / 2, yPosition, {
      align: 'center',
    });
    yPosition += 12;

    // Divider line
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
  };

  const addFooter = (doc: jsPDF, pageWidth: number) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;

    // Footer line
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, pageHeight - 30, pageWidth - margin, pageHeight - 30);

    // Footer text
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(
      'BetterCapital.us - Real Estate Investment Management Platform',
      pageWidth / 2,
      pageHeight - 20,
      { align: 'center' },
    );
    doc.text(
      `Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`,
      pageWidth / 2,
      pageHeight - 15,
      { align: 'center' },
    );
  };

  const renderAssetsTable = () => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Property Name</TableHead>
          <TableHead>Address</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Purchase Price</TableHead>
          <TableHead>Market Value</TableHead>
          <TableHead>Net Cash Flow</TableHead>
          <TableHead>Cap Rate</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {(data as Asset[]).map((asset: Asset) => (
          <TableRow key={asset.id}>
            <TableCell className="font-medium">{asset.name}</TableCell>
            <TableCell>{asset.address}</TableCell>
            <TableCell>
              <Badge variant="outline">{asset.asset_type}</Badge>
            </TableCell>
            <TableCell>{formatCurrency(asset.purchase_price)}</TableCell>
            <TableCell>{formatCurrency(asset.market_value)}</TableCell>
            <TableCell
              className={
                asset.net_cash_flow >= 0 ? 'text-green-600' : 'text-red-600'
              }
            >
              {formatCurrency(asset.net_cash_flow)}
            </TableCell>
            <TableCell>{asset.cap_rate?.toFixed(2) || '0.00'}%</TableCell>
            <TableCell>
              <Badge
                variant={
                  asset.investment_status === 'active' ? 'default' : 'secondary'
                }
              >
                {asset.investment_status}
              </Badge>
            </TableCell>
            <TableCell>
              <Button variant="ghost" size="sm">
                <Edit className="h-4 w-4" />
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  const renderTransactionsTable = () => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Date</TableHead>
          <TableHead>Property</TableHead>
          <TableHead>Description</TableHead>
          <TableHead>Category</TableHead>
          <TableHead>Amount</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Payee</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {(data as Transaction[]).map((transaction: Transaction) => (
          <TableRow key={transaction.id}>
            <TableCell>{formatDate(transaction.date)}</TableCell>
            <TableCell className="font-medium">
              {transaction.property_name}
            </TableCell>
            <TableCell>{transaction.description}</TableCell>
            <TableCell>
              <Badge variant="outline">{transaction.category}</Badge>
            </TableCell>
            <TableCell
              className={
                transaction.transaction_type === 'INCOME'
                  ? 'text-green-600'
                  : 'text-red-600'
              }
            >
              {transaction.transaction_type === 'INCOME' ? '+' : '-'}
              {formatCurrency(Math.abs(transaction.amount))}
            </TableCell>
            <TableCell>
              <Badge
                variant={
                  transaction.transaction_type === 'INCOME'
                    ? 'default'
                    : 'destructive'
                }
              >
                {transaction.transaction_type}
              </Badge>
            </TableCell>
            <TableCell>{transaction.payee}</TableCell>
            <TableCell>
              <Badge
                variant={
                  transaction.status === 'completed' ? 'default' : 'secondary'
                }
              >
                {transaction.status}
              </Badge>
            </TableCell>
            <TableCell>
              <Button variant="ghost" size="sm">
                <Edit className="h-4 w-4" />
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  const renderFinancialsTable = () => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Property</TableHead>
          <TableHead>Purchase Price</TableHead>
          <TableHead>Market Value</TableHead>
          <TableHead>Total Income</TableHead>
          <TableHead>Total Expenses</TableHead>
          <TableHead>Net Cash Flow</TableHead>
          <TableHead>Cap Rate</TableHead>
          <TableHead>Cash on Cash</TableHead>
          <TableHead>Appreciation</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {(data as FinancialSummary[]).map((financial: FinancialSummary) => (
          <TableRow key={financial.id}>
            <TableCell className="font-medium">
              {financial.property_name}
            </TableCell>
            <TableCell>{formatCurrency(financial.purchase_price)}</TableCell>
            <TableCell>{formatCurrency(financial.market_value)}</TableCell>
            <TableCell className="text-green-600">
              {formatCurrency(financial.total_income)}
            </TableCell>
            <TableCell className="text-red-600">
              {formatCurrency(financial.total_expenses)}
            </TableCell>
            <TableCell
              className={
                financial.net_cash_flow >= 0 ? 'text-green-600' : 'text-red-600'
              }
            >
              {formatCurrency(financial.net_cash_flow)}
            </TableCell>
            <TableCell>{financial.cap_rate?.toFixed(2) || '0.00'}%</TableCell>
            <TableCell>
              {financial.cash_on_cash_return?.toFixed(2) || '0.00'}%
            </TableCell>
            <TableCell
              className={
                (financial.appreciation_percentage || 0) >= 0
                  ? 'text-green-600'
                  : 'text-red-600'
              }
            >
              {(financial.appreciation_percentage || 0).toFixed(2)}%
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <div className="container mx-auto space-y-6">
      <div className="flex justify-end">
        <Button onClick={handleExport} variant="outline">
          <Download className="h-4 w-4 mr-2" />
          Export PDF
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="assets">Properties</TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="financials">Financial Summary</TabsTrigger>
        </TabsList>

        <TabsContent value="assets" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Property Filters
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                <div className="flex-1">
                  <Input
                    placeholder="Search properties..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="max-w-sm"
                  />
                </div>
                <Select
                  value={filters.status || 'all'}
                  onValueChange={(value) =>
                    setFilters({
                      ...filters,
                      status: value === 'all' ? '' : value,
                    })
                  }
                >
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={filters.propertyType || 'all'}
                  onValueChange={(value) =>
                    setFilters({
                      ...filters,
                      propertyType: value === 'all' ? '' : value,
                    })
                  }
                >
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Property Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="single_family">Single Family</SelectItem>
                    <SelectItem value="multi_family">Multi Family</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center">Loading properties...</div>
              ) : (
                renderAssetsTable()
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="transactions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Transaction Filters
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Input
                  placeholder="Search transactions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <Select
                  value={filters.transactionType || 'all'}
                  onValueChange={(value) =>
                    setFilters({
                      ...filters,
                      transactionType: value === 'all' ? '' : value,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="INCOME">Income</SelectItem>
                    <SelectItem value="EXPENSE">Expense</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={filters.category || 'all'}
                  onValueChange={(value) =>
                    setFilters({
                      ...filters,
                      category: value === 'all' ? '' : value,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {predefinedCategories.map((category) => (
                      <SelectItem key={category.value} value={category.value}>
                        {category.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="date"
                  placeholder="Start Date"
                  onChange={(e) =>
                    setFilters({ ...filters, startDate: e.target.value })
                  }
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center">Loading transactions...</div>
              ) : (
                renderTransactionsTable()
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="financials" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Financial Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                <Select
                  value={filters.period || 'annual'}
                  onValueChange={(value) =>
                    setFilters({ ...filters, period: value })
                  }
                >
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Period" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="annual">Annual</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  type="date"
                  placeholder="Start Date"
                  onChange={(e) =>
                    setFilters({ ...filters, startDate: e.target.value })
                  }
                />
                <Input
                  type="date"
                  placeholder="End Date"
                  onChange={(e) =>
                    setFilters({ ...filters, endDate: e.target.value })
                  }
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center">
                  Loading financial summary...
                </div>
              ) : (
                renderFinancialsTable()
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <span className="flex items-center px-4">
            Page {currentPage} of {pagination.totalPages}
          </span>
          <Button
            variant="outline"
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === pagination.totalPages}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
